import { ChangeDetectionStrategy, Component, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, first, of, tap } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSuffix } from '@angular/material/form-field';
import { EntriesService } from 'app/features/admin/modules/features/entries/entries.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { isSecureMask, SECURE_MASK, secureVaultFieldName } from './secure-input-mask.util';


@Component({
  selector: 'formly-field-secure-input',
  template: `
    <div>
      <!--
        Width classes from field.className are already applied by formly to the host
        <formly-field>. Re-applying them here compounded them (a half-width field rendered at
        50% of 50%); the inner field just fills its host.
      -->
      <mat-form-field
        class="dd-compact-field w-full"
        [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
        <mat-label>
          <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
        </mat-label>

        <div class="flex items-center w-full">
          <input
            matInput
            [type]="showValue || isStoredMask() ? 'text' : 'password'"
            [class.dd-mask-tail-tabular]="isStoredMask()"
            [required]="props.required"
            [disabled]="props.disabled || isLoading()"
            [formControl]="formControl"
            [formlyAttributes]="field"
            [formlyDataCy]="field"
            (focus)="clearMaskOnFocus($event)"
            (blur)="restoreMaskOnBlur($event)" />
        </div>
        <span
          matSuffix
          class="inline-flex items-center">
          @if (hasEntryId() && !valueLoaded()) {
            <button
              class="leading-6"
              type="button"
              mat-icon-button
              [disabled]="isDisabled() || isLoading()"
              (click)="loadSecureValue()"
              aria-label="Beveiligde waarde laden"
              matTooltip="Beveiligde waarde laden"
              [matTooltipDisabled]="isDisabled()">
              @if (isLoading()) {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                <mat-icon>visibility</mat-icon>
              }
            </button>
          } @else {
            <button
              type="button"
              mat-icon-button
              [disabled]="isDisabled()"
              (click)="toggleShowValue()"
              [attr.aria-label]="showValue ? 'Verberg waarde' : 'Toon waarde'"
              [matTooltip]="showValue ? 'Verberg waarde' : 'Toon waarde'"
              [matTooltipDisabled]="isDisabled()">
              <mat-icon>{{ showValue ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          }
        </span>

        @if (props.description) {
          <mat-hint>{{ props.description }}</mat-hint>
        }
        @if (!props.description && hasEntryId() && !valueLoaded()) {
          <mat-hint> Klik op het oog-icoon om de beveiligde waarde te bekijken </mat-hint>
        }
        @if (!props.description && !hasEntryId()) {
          <mat-hint> Deze waarde wordt veilig opgeslagen in een aparte kluis </mat-hint>
        }
      </mat-form-field>

      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  // Only the digits/tail of a portal-written mask ('••••2735') should align like a document
  // number. No color here (only via tokens) — the property is purely typographic.
  styles: [
    `
      .dd-mask-tail-tabular {
        font-variant-numeric: tabular-nums;
      }
    `,
  ],
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    FormlyErrorComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSuffix,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecureInputComponent extends FieldType implements OnInit {
  private entriesService = inject(EntriesService);

  isLoading = signal(false);
  error = signal('');
  valueLoaded = signal(false);
  showValue = false;

  /**
   * True from the moment a stored mask is focused until blur ends the edit. Set synchronously in
   * `clearMaskOnFocus` itself — never derived from the control's value — because the control keeps
   * holding the mask for a beat after focus (only the DOM input is cleared there). Deriving this
   * state from the value alone would leave `[type]` at `'text'` through that beat, letting the
   * user's first real keystroke land in a plain-text box before the (input) event flips it to
   * password.
   */
  #isEditingMaskedValue = false;

  ngOnInit() {
    // If we have an entry ID, show a mask until the eye is clicked. A masked reminder the entry
    // already carries (`••••2735`, props.visibleTailLength) IS that mask: keeping it means an
    // untouched field saves its reminder back unchanged instead of overwriting it with bullets.
    if (this.hasEntryId() && !isSecureMask(this.formControl.value)) {
      this.formControl.setValue(SECURE_MASK);
      this.formControl.markAsPristine();
    }
  }

  /**
   * A mask is never something to edit: typing behind `••••2735` produced `••••2735mm`, which
   * `processSecureFields` rightly takes for an untouched mask (it starts with a bullet) — so the
   * junk was stored as the reminder and the vault never got a new value. Only the DOM input
   * empties on focus — the FormControl itself is left untouched — so whatever the user types
   * becomes the whole new value through the normal CVA (input event) path, exactly as if the
   * field had started empty. Leaving the control alone also means a save that reads the raw
   * FormControl value without blurring first (Ctrl/Cmd+S, `DetailsComponent.handleKeyboardShortcut`)
   * still sees the mask, not an emptied-out value that would overwrite the stored reminder.
   */
  clearMaskOnFocus(event: FocusEvent): void {
    const value = this.formControl.value;
    if (this.isDisabled() || !isSecureMask(value)) {
      return;
    }
    // Flip to password BEFORE clearing the DOM value — no keystroke can land before this handler
    // returns, so there is no frame where a typed character shows in a type="text" box.
    this.#isEditingMaskedValue = true;
    (event.target as HTMLInputElement).value = '';
  }

  /**
   * Nothing typed: the DOM input shows the stored mask again, matching what the FormControl has
   * held all along (it was never changed on focus, so there is nothing to restore there). The edit
   * always ends on blur, typed or not — when something WAS typed the control no longer holds a
   * mask anyway, so `isStoredMask()` already reads false regardless of this flag.
   */
  restoreMaskOnBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = this.formControl.value;
    this.#isEditingMaskedValue = false;
    if (input.value === '' && isSecureMask(value)) {
      input.value = value;
    }
  }

  hasEntryId(): boolean {
    return !!this.options?.formState?.entry?.id;
  }

  toggleShowValue(): void {
    if (this.isDisabled()) {
      return;
    }
    this.showValue = !this.showValue;
  }

  loadSecureValue(): void {
    if (this.isDisabled()) {
      return;
    }
    this.isLoading.set(true);
    this.error.set('');

    const fieldName = secureVaultFieldName(this.field);

    if (!fieldName) {
      this.error.set('Veldnaam kan niet worden bepaald');
      this.isLoading.set(false);
      return;
    }

    const moduleId = this.options.formState.entity.id;
    const entryId = this.options.formState.entry.id;

    this.entriesService
      .getSecureField(moduleId, entryId, fieldName)
      .pipe(
        first(),
        tap((response: any) => {
          // Handle the secure value based on response format
          if (response) {
            response = JSON.parse(response);
            this.formControl.setValue(response.value);
          } else {
            this.formControl.setValue('');
          }

          // Mark as loaded and show the value
          this.valueLoaded.set(true);
          this.showValue = true;
          this.formControl.markAsPristine(); // Don't mark as dirty when just viewing
        }),
        catchError(err => {
          if (err.status === 404) {
            this.error.set('Geen beveiligde waarde gevonden voor dit veld');
          } else {
            this.error.set(err.message || 'Fout bij het laden van de beveiligde waarde');
          }
          return of(null);
        })
      )
      .subscribe(() => {
        this.isLoading.set(false);
      });
  }

  isDisabled(): boolean {
    return this.props.disabled === true || this.formControl.disabled;
  }

  /**
   * A value the portal wrote itself as a mask (the stored tail `••••2735`, or the load
   * placeholder) is not a secret being hidden — it was written specifically to be shown. Only a
   * value that could actually BE a typed secret renders as password dots. While the mask is being
   * edited (focused, before blur restores or replaces it) it renders as password regardless of the
   * control's value, since that value lags a beat behind the DOM during the edit.
   */
  isStoredMask(): boolean {
    return !this.#isEditingMaskedValue && isSecureMask(this.formControl.value);
  }
}
